import React from "react";
import styled, { useTheme } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle } from "lucide-react";
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0; // Não é prop customizada, é CSS
  right: 0;
  bottom: 0;
  background: ${props => (props.theme.isLight ? 'rgba(16,24,32,0.06)' : props.theme.colors.overlay)};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  width: 90%;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ActionButton = styled.button<{ $variant?: "primary" | "danger" }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${(props) => {
    switch (props.$variant) {
      case "danger":
        return `
          background: ${props.theme.colors.error};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            background: ${props.theme.colors.error}dd;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundSecondary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundTertiary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}
`;

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  loading = false,
}) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const theme = useTheme();

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <ModalContent
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.2 }} // Framer Motion, não styled-components
          >
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={20} color={theme.colors.error} />
                {title}
              </ModalTitle>
              <CloseButton onClick={onClose}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>{message}</ModalBody>
            <ModalFooter>
              <ActionButton onClick={onClose} disabled={loading}>
                {cancelText}
              </ActionButton>
              <ActionButton
                $variant="danger"
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? "Excluindo..." : confirmText}
              </ActionButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
